export const AUTO_FIT_MAX_WIDTH = 480;
export const COLUMN_KEY_ATTRIBUTE = 'data-column-key';

export interface AutoFitBounds {
  min: number;
  max: number;
}

/** Picks the width that shows the widest measured cell in full, within `bounds`. */
export function resolveAutoFitWidth(
  measured: readonly number[],
  bounds: AutoFitBounds
): number {
  const widest = measured.reduce((max, width) => Math.max(max, width), 0);

  return Math.min(Math.max(Math.ceil(widest), bounds.min), bounds.max);
}

/**
 * Widths the given elements would take if nothing constrained them, in the same order.
 *
 * Measured on off-screen clones rather than on the elements themselves: cells sit in a flex
 * container with `overflow-hidden`, so their content shrinks instead of overflowing and
 * `scrollWidth` only ever reports the width the grid already granted.
 */
export function measureNaturalWidths(elements: readonly Element[]): number[] {
  if (elements.length === 0) {
    return [];
  }

  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  // `nowrap` collapses the default cell's `line-clamp-3` onto one line.
  probe.style.cssText =
    'position:fixed;top:-10000px;left:0;visibility:hidden;pointer-events:none;white-space:nowrap';

  const clones = elements.map((element) => {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.width = 'max-content';
    clone.style.height = 'auto';
    probe.appendChild(clone);
    return clone;
  });

  document.body.appendChild(probe);

  try {
    return clones.map((clone) => clone.getBoundingClientRect().width);
  } finally {
    probe.remove();
  }
}
